<template>
  <div>
    <p class="text-grey-800 text--large text--bold">
      {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddDocumentsSelection.title') }}
    </p>
    <p class="text-grey-700 mb-3 text--small">
      {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddDocumentsSelection.subTitle') }}
    </p>

    <AppAlertInPage v-if="!roomProvidersPending">
      {{ documentsMaxSizeWarning }}
    </AppAlertInPage>

    <SigningChecklistDocumentsSelector class="mb-8" @select="selectDocuments"/>

    <div v-if="envelopeTemplateDrafts.length > 0" class="mt-8 mb-4">
      <p
        class="text--medium text--bold mb-4"
        v-t="{
         path: 'project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddDocumentsSelection.selectedDocuments',
         plural: envelopeTemplateDrafts.length,
         args: { count: envelopeTemplateDrafts.length },
       }"/>

      <SigningChecklistSelectedDocumentsListItem
        v-for="(templateDraft, index) in envelopeTemplateDrafts"
        :key="`file-${index}`"
        :file="templateDraft.document"
        can-delete
        @deleteFile="removeFileFromSelection(templateDraft.document)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import { typedMapMutations } from '@/common/utils/store'
import AppAlertInPage from '@/design-system/alert/AppAlertInPage.vue'
import SigningChecklistDocumentsSelector from '@/project/signing-checklist/SigningChecklistDocumentsSelector.vue'
import SigningChecklistSelectedDocumentsListItem from '@/project/signing-checklist/SigningChecklistSelectedDocumentsListItem.vue'
import type { EnvelopeTemplatesMutationType } from '@/store/modules/envelope-templates/mutation_types'
import { PROCESS_ENVELOPE_DOCUMENTS_UPLOAD_QUEUE } from '@/store/modules/signing-checklist/action_types'
import { ADD_ENVELOPE_DOCUMENTS_TO_UPLOAD_QUEUE } from '@/store/modules/signing-checklist/mutation_types'

export default defineComponent({
  name: 'EnvelopeTemplateAddDocumentsSelection',
  components: {
    AppAlertInPage,
    SigningChecklistDocumentsSelector,
    SigningChecklistSelectedDocumentsListItem,
  },
  computed: {
    ...mapState('envelopeTemplates', ['envelopeTemplateDrafts']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    ...mapGetters('room', ['isCurrentUserPm', 'maxSignableSize', 'roomMnemo']),
    documentsMaxSizeWarning (): string {
      const allowWordSigning = import.meta.env.VUE_APP_ENABLE_WORD_SIGNING
      const maxSignablePages = import.meta.env.VUE_APP_MAX_SIGNABLE_PAGES

      return this.$t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddDocumentsSelection.maxFileSizeWarning.warning', {
        fileExtensions: allowWordSigning
          ? this.$t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddDocumentsSelection.maxFileSizeWarning.wordOrPDF')
          : this.$t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddDocumentsSelection.maxFileSizeWarning.pdf'),
        maxSignableSize: this.maxSignableSize,
        maxPages: maxSignablePages,
      }).toString()
    },
  },
  methods: {
    ...mapActions('signingChecklist', [PROCESS_ENVELOPE_DOCUMENTS_UPLOAD_QUEUE]),
    ...mapMutations('signingChecklist', [ADD_ENVELOPE_DOCUMENTS_TO_UPLOAD_QUEUE]),
    ...typedMapMutations<EnvelopeTemplatesMutationType>(
      'envelopeTemplates',
      [
        'ADD_DOCUMENTS_TO_TEMPLATE_DRAFT',
        'REMOVE_DOCUMENT_FROM_TEMPLATE_DRAFT',
        'SET_TEMPLATE_DOCUMENT_DATA_AFTER_UPLOAD_ERROR',
        'SET_TEMPLATE_DOCUMENT_DATA_AFTER_UPLOAD_SUCCESS',
        'SET_TEMPLATE_DOCUMENT_UPLOAD_PROGRESS',
      ],
    ),
    selectDocuments ({ files, fromClosd }: { files: Array<any>, fromClosd: boolean }) {
      this.ADD_DOCUMENTS_TO_TEMPLATE_DRAFT(files)

      if (!fromClosd) {
        this.uploadExternalFiles(files)
      }
    },
    removeFileFromSelection (fileToRemove: any) {
      if (fileToRemove.cancelToken) {
        fileToRemove.cancelToken.cancel()
      }

      this.REMOVE_DOCUMENT_FROM_TEMPLATE_DRAFT(fileToRemove)
    },
    uploadExternalFiles (externalFiles: Array<any>) {
      this.ADD_ENVELOPE_DOCUMENTS_TO_UPLOAD_QUEUE(externalFiles)

      const documentUploadCallbacks = {
        onEachSuccessCallback: (uploadedDocument: any, queueId: string) => {
          this.SET_TEMPLATE_DOCUMENT_DATA_AFTER_UPLOAD_SUCCESS({ uploadedDocument, queueId })
        },
        onEachUploadProgressCallback: (queueId: string, uploadProgression: number) => {
          this.SET_TEMPLATE_DOCUMENT_UPLOAD_PROGRESS({ queueId, uploadProgression })
        },
        onEachErrorCallback: (queueId: string) => {
          this.SET_TEMPLATE_DOCUMENT_DATA_AFTER_UPLOAD_ERROR(queueId)
        },
      }
      this.PROCESS_ENVELOPE_DOCUMENTS_UPLOAD_QUEUE(documentUploadCallbacks)
    },
  },
})
</script>
